import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from "@angular/core";
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  FormArray,
} from "@angular/forms";
import { PatientSearch } from "../../model/patient/patient-search";
import { PatientService } from "../../services/patient.service";
import { NotificationToastService } from "../../services/notification-toast.service";
import { PatientView } from "../../model/patient/patient-view";
import { PatientEditDialogComponent } from "../dialogs/patient-edit/patient-edit-dialog.component";
import { MatDialog } from "@angular/material";
import { AuthService } from "../../services/auth.service";
import { BillingInvoiceEntityEditService } from "../../services/billing/billing-invoice/billing-invoice-entity-edit.service";
import { PatientChangeAutogestionPassDialogComponent } from "../dialogs/patient-change-autogestion-pass/patient-change-autogestion-pass.component";
import { MedicalAppointmentAuditingService } from "src/app/services/medical-appointment-auditing/medical-appointment-auditing.service";

@Component({
  selector: "app-patient-view-component",
  templateUrl: "./patient-view.component.html",
  styleUrls: ["./patient-view.component.css"],
})
export class PatientViewComponent implements OnInit, OnChanges {
  @Output() patientChange = new EventEmitter();
  @Output() auditChange = new EventEmitter<boolean>();
  @Input() patientview: PatientView;
  @Input() patient: PatientSearch;
  @Input() loadingAuditPatient: boolean;
  @Input() readOnly: boolean;
  @Input() isAuditing: boolean;
  @Input() isPatientDataValidated: boolean;
  public patientViewFormGroup: FormGroup;
  canViewMedicalPatientInfo: boolean;
  seePatientContactInfo: boolean;
  loading = false;
  freeDiagnostic: string;

  constructor(
    private notificationToastService: NotificationToastService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private authService: AuthService,
    private patientService: PatientService
  ) {
    this.seePatientContactInfo = this.authService.seePatientContactInfo();

    this.patientViewFormGroup = this.formBuilder.group({
      CredentialNumber: new FormControl({ value: "", disabled: true }),
      InAutoGestion: new FormControl({ value: "", disabled: true }),
      ViewResultWeb: new FormControl({ value: null, disabled: true }),
      DocumentType: new FormControl({ value: "", disabled: true }),
      Number: new FormControl({ value: "", disabled: true }),
      LastName: new FormControl({ value: "", disabled: true }),
      Diagnostics: new FormControl({ value: "", disabled: true }),
      FirstName: new FormControl({ value: "", disabled: true }),
      Pid: new FormControl({ value: "", disabled: true }),
      Relevance: new FormControl({ value: "", disabled: true }),
      BirthDate: new FormControl({ value: "", disabled: true }),
      Sex: new FormControl({ value: "", disabled: true }),
      Relevances: this.formBuilder.array([]),
      Country: new FormControl({ value: "", disabled: true }),
      Province: new FormControl({ value: "", disabled: true }),
      Locality: new FormControl({ value: "", disabled: true }),
      AddressStreet: new FormControl({ value: "", disabled: true }),
      AddressNumber: new FormControl({ value: "", disabled: true }),
      Floor: new FormControl({ value: "", disabled: true }),
      AddressDpto: new FormControl({ value: "", disabled: true }),
      AddressCP: new FormControl({ value: "", disabled: true }),
      PersonalPhone: new FormControl({ value: "", disabled: true }),
      LaborPhone: new FormControl({ value: "", disabled: true }),
      CellPhone: new FormControl({ value: "", disabled: true }),
      Email: new FormControl({ value: "", disabled: true }),
      Weight: new FormControl({ value: "", disabled: true }),
      Height: new FormControl({ value: "", disabled: true }),
      Claustrophobia: new FormControl({ value: "", disabled: true }),
      Pacemaker: new FormControl({ value: "", disabled: true }),
      MetallicPart: new FormControl({ value: "", disabled: true }),
      DisabilityPermanent: new FormControl({ value: "", disabled: true }),
      DisabilityTemporal: new FormControl({ value: "", disabled: true }),
      EmergencyPhone: new FormControl({ value: "", disabled: true }),
      MotherFirstName: new FormControl({ value: "", disabled: true }),
      MotherLastName: new FormControl({ value: "", disabled: true }),
      FatherFirstName: new FormControl({ value: "", disabled: true }),
      FatherLastName: new FormControl({ value: "", disabled: true }),
      Entity: new FormControl({ value: "", disabled: true }),
      Plan: new FormControl({ value: "", disabled: true }),
      AliquotTax: new FormControl({ value: "", disabled: true }),
      CoordinatorPhysician: new FormControl({ value: "", disabled: true }),
      Physicians: new FormControl({ value: "", disabled: true }),
      PracticeRegion: new FormControl({ value: "", disabled: true }),
    });
  }

  validateControlled() {
    this.auditChange.emit(true);
  }

  revertControlled() {
    this.auditChange.emit(false);
  }

  get canChangePasswordAutogesion() {
    return this.authService.canChangePasswordAutogesion();
  }
  get f() {
    return this.patientViewFormGroup.controls;
  }

  async ngOnChanges(changes: SimpleChanges) {
    await this.setPatient();
  }

  public refresh(patientSearch: PatientSearch): void {
    this.patientview = null;
    this.patient = patientSearch;
    this.setPatient();
  }

  public async setPatient() {
    if (this.patientViewFormGroup) {
      try {
        this.loading = true;

        if (
          !this.patientview ||
          (this.patient &&
            (!this.patientview || this.patientview.Id !== this.patient.Id))
        ) {
          if (this.patient) {
            this.patientview = await this.patientService.getPatientView(
              this.patient.Id
            );
            this.patientChange.emit(this.patientview);
          }
        }

        if (this.patientview) {
          if (!this.patientview.Relevance) {
            this.patientview.Relevance = [];
          }

          if (!this.patientview.Diagnostics) {
            this.patientview.Diagnostics = [];
          }

          this.freeDiagnostic = this.patientview.FreeDiagnostic;

          this.patientViewFormGroup.patchValue({
            InAutoGestion: this.patientview.InAutoGestion,
            ViewResultWeb: this.patientview.ViewResultWeb,
            DocumentType: this.patientview.DocumentType,
            Number: this.patientview.Number,
            LastName: this.patientview.LastName,
            FirstName: this.patientview.FirstName,
            Pid: this.patientview.Id,
            BirthDate: this.patientview.BirthDate,
            Sex: this.patientview.Sex,
            CredentialNumber: this.patientview.CredentialNumber,
            Country: this.patientview.Country,
            EmergencyPhone: this.patientview.EmergencyPhone,
            Relevance: this.patientview.Relevance.join(","),
            Diagnostics: this.patientview.Diagnostics.join(","),
            Province: this.patientview.Province,
            Locality: this.patientview.Locality,
            AddressStreet: this.patientview.AddressStreet,
            AddressNumber: this.patientview.AddressNumber,
            Floor: this.patientview.Floor,
            AddressDpto: this.patientview.AddressDpto,
            AddressCP: this.patientview.AddressCP,
            PersonalPhone: this.patientview.PersonalPhone,
            LaborPhone: this.patientview.LaborPhone,
            CellPhone: this.patientview.CellPhone,
            Email: this.patientview.Email,
            Weight: this.patientview.Weight,
            Height: this.patientview.Height,
            Claustrophobia: this.patientview.Claustrophobia,
            Pacemaker: this.patientview.Pacemaker,
            MetallicPart: this.patientview.MetallicPart,
            DisabilityPermanent: this.patientview.DisabilityPermanent,
            DisabilityTemporal: this.patientview.DisabilityTemporal,
            MotherFirstName: this.patientview.MotherFirstName,
            MotherLastName: this.patientview.MotherLastName,
            FatherFirstName: this.patientview.FatherFirstName,
            FatherLastName: this.patientview.FatherLastName,
            Entity: this.patientview.Entity,
            Plan: this.patientview.Plan,
            AliquotTax: this.patientview.AliquotTax,
          });
        }

        this.patientService.patientSelected(this.patientview);
      } catch (err) {
        console.log(err);
        this.notificationToastService.error(
          JSON.stringify(err.error),
          "Paciente"
        );
      } finally {
        this.loading = false;
      }
    }
  }

  changePasswordAutogesion(): void {
    if (this.patientview) {
      this.dialog.open(PatientChangeAutogestionPassDialogComponent, {
        width: "50%",
        data: {
          patientId: this.patientview.Id,
          firstName: this.patientview.FirstName,
          lastName: this.patientview.LastName,
          document: this.patientview.Number,
        },
      });
    }
  }

  edit(): void {
    if (this.patientview) {
      const dialogRef = this.dialog.open(PatientEditDialogComponent, {
        width: "90%",
        data: { patientId: this.patientview.Id },
      });

      dialogRef.afterClosed().subscribe((patientSearch: PatientSearch) => {
        if (patientSearch) {
          this.refresh(patientSearch);
        }
      });
    }
  }

  ngOnInit() {
    this.authService.canViewMedicalPatientInfo().then((value) => {
      this.canViewMedicalPatientInfo = value;
    });

    if (this.patient) {
      this.setPatient();
    }
  }
}
